/**
 * External dependencies
 */

import React, { useEffect, useState } from 'react';

/**
 * DWT dependencies
 */
import { trackShareClick } from '../../analytics';
import useMediaQuery from '../../hooks/useMediaQuery';
import {
  checkCircle,
  copyIcon,
  facebookIcon,
  mailIcon,
  pinterestIcon2,
  playButton,
  prhLogoBlack,
  prhaLogoColor,
  shareArrowIcon,
  smsIcon,
  twitterIcon
} from '../../img/icons';
import { handleAudioContributors, handleBookCoverImage, handleBookReadMoreURL } from '../utils/component-utils';

/**
 * ShareModalMain
 *
 * Book main content for modal
 *
 * @param {Object} props
 * @param {Object} props.context
 * @param {Object} props.modalData
 *
 */

const ShareModalMain = ({ context, modalData }) => {
  const [ text, setText ] = useState( '' );
  const [ activeEmbed, setActiveEmbed ] = useState( 'mini' );

  const [ linkCopySuccess, setLinkCopySuccess ] = useState( false );
  const [ htmlCopySuccess, setHtmlCopySuccess ] = useState( false );
  const title = modalData?.title;
  const getAudioContributors = handleAudioContributors( modalData, context?.entry === 'automatic' );
  let narratorsString = '';
  if ( Array.isArray( modalData?.author ) ) {
    const narrators = modalData.author.filter( ( author ) => author.roleCode === '4' );
    narratorsString = narrators.map( ( narrator ) => narrator.authorDisplay ).join( ', ' );
  }
  const author = getAudioContributors || modalData?.author;
  const encodedTitle = decodeURIComponent( title ) !== title ? title : encodeURIComponent( title );

  const getCoverImage = handleBookCoverImage( modalData, context?.entry );
  const bookUrl = handleBookReadMoreURL( modalData );
  const isbn = modalData?.isbnStr || modalData?.isbn;

  const isDesktop = useMediaQuery( '( min-width: 920px )' );
  const hasAudio = !!( ( modalData?.audioUrl || ( modalData?.asset_types && modalData?.asset_types[18]?.url ) ) );

  const icons = {
    facebook: {
      name: 'Facebook',
      icon: facebookIcon,
      url: `https://www.facebook.com/sharer/sharer.php?u=${bookUrl}`,
    },
    pinterest: {
      name: 'Pinterest',
      icon: pinterestIcon2,
      url: `https://www.pinterest.com/pin/create/button/?url=${bookUrl}&media=${getCoverImage}&description=${encodedTitle}`,
    },
    twitter: {
      name: 'Twitter',
      icon: twitterIcon,
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}%20-%20${bookUrl}`,
    },
    email: {
      name: 'Email',
      icon: mailIcon,
      url: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%20-%20${bookUrl}`,
    },
    sms: {
      name: 'SMS',
      icon: smsIcon,
      url: `sms:?&body=${encodedTitle}%20-%20${bookUrl}`,
    },
  };

  const copyToClipboard = ( dataToCopy ) => {
    navigator.clipboard.writeText( dataToCopy );
    dataToCopy.includes( '<iframe' )
      ? setHtmlCopySuccess( true )
      : setLinkCopySuccess( true );
    setTimeout( () => {
      dataToCopy.includes( '<iframe' )
        ? setHtmlCopySuccess( false )
        : setLinkCopySuccess( false );
    }, 3000 );
  };

  const miniIframe = `<iframe width="320" height="388" src="https://www.penguinrandomhouseaudio.com/embed/mini/${isbn}?ref=PRHA9F7585CC7" frameborder="0"></iframe>`;
  const bigIframe = `<iframe width="100%" height="200" src="https://www.penguinrandomhouseaudio.com/embed/full/${isbn}?ref=PRHA9F7585CC7" frameborder="0"></iframe>`;
  // toggle between square/mini & rectangle/big embed options
  const toggleEmbed = ( selectedEmbed ) => {
    setActiveEmbed( selectedEmbed );
    if ( selectedEmbed === 'mini' ) {
      setText( miniIframe );
    } else {
      setText( bigIframe );
    }
  };

  // rerender for each new audiobook
  useEffect( () => {
    setText( miniIframe );
  }, [ modalData ] );

  // if screen is resized while 'rectangle' is selected, force use of 'square'
  useEffect( () => {
    if ( !isDesktop && activeEmbed === 'big' ) {
      toggleEmbed( 'mini' );
    }
  }, [ isDesktop ] );

  return (
    <>
      <div className="modal__main">
        {linkCopySuccess && (
          <div className="copy-toast">Link Copied</div>
        )}
        {htmlCopySuccess && (
          <div className="copy-toast">HTML Copied</div>
        )}

        <div className="prev-modal__details-view">
          <div className="modal__embed-share">
            <div className="modal__share">
              <h3>Share a link to info about the audiobook</h3>
              <hr style={{ marginTop: '10px' }} />
              <div className="share__actions">
                <button
                  type="button"
                  data-copy-value={bookUrl}
                  onClick={( e ) =>
                    copyToClipboard(
                      e.target.getAttribute(
                        'data-copy-value'
                      )
                    )
                  }
                  className="share__button"
                >
                  {copyIcon} Copy Link
                </button>
                <ul className="share__items">
                  {Object.values( icons ).map( ( item, index ) => (
                    <li
                      key={index}
                      className="share__item-icon"
                    >
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        id={item.name}
                        aria-label={item.name}
                        onClick={() => trackShareClick( modalData, item.name, 'audio' )}
                      >
                        {item.icon}
                      </a>
                    </li>
                  ) )}
                </ul>
              </div>
            </div>
            { hasAudio && (
              <div className="modal__embed">
                <h3>Embed an audio player in your website</h3>
                <hr style={{ marginTop: '10px' }} />
                <div className="embed__wrapper">
                  <div className="embed-format">
                    {isDesktop && (
                      <fieldset>
                        <legend id="embed-format-legend" className="share__subheader">
                          Select Format:
                        </legend>
                        <div className="embed-format-nav">
                          <label 
                            className={`embed-format ${
                              activeEmbed === 'mini'
                                ? 'active'
                                : ''
                            }`} 
                            id="mini-label"
                          >
                            <input
                              type="radio"
                              name="embed-format"
                              value="mini"
                              checked={activeEmbed === 'mini'}
                              onChange={() => toggleEmbed( 'mini' )}
                              className="sr-only peer"
                              aria-labelledby="embed-format-legend mini-label"
                            />
                            <div className='p-2 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-blue-500 peer-focus-visible:rounded-sm flex flex-col items-start'>
                              <div className='text-checkmark-wrapper square flex items-center'>
                                {activeEmbed === 'mini' && checkCircle}
                                <p>Square</p>
                              </div>
                              <div className="embed-preview-icon mini ">
                                <span className="embed-format-big-left"></span>
                              </div>
                            </div>
                          </label>
                          <label
                            className={`embed-format ${
                              activeEmbed === 'big'
                                ? 'active'
                                : ''
                            }`}
                            id="big-label"
                          >
                            <input
                              type="radio"
                              name="embed-format"
                              value="big"          
                              checked={activeEmbed === 'big'}
                              onChange={() => toggleEmbed( 'big' )}
                              className="sr-only peer"
                              aria-labelledby="embed-format-legend big-label"
                            />
                            <div className='p-2 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-blue-500 peer-focus-visible:rounded-sm flex flex-col items-start'>
                              <div className='text-checkmark-wrapper rectangle flex items-center'>
                                {activeEmbed === 'big' && checkCircle}
                                <p>Rectangle</p>
                              </div>
                              <div className="embed-preview-icon big">
                                <span className="embed-format-big-left"></span>
                                <span className="embed-format-big-right">
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                </span>
                              </div>
                            </div>
                          </label>
                        </div>
                      </fieldset>
                    )}

                    <div className="embed-preview-text">
                      <span className="share__subheader">
                        Copy and paste the following HTML
                        into your website code:
                      </span>
                      <label htmlFor="embed-html-textarea" className="sr-only">
                       Copy HTML Code
                      </label>
                      <textarea
                        rows="3"
                        id="embed-html-textarea"
                        value={text}
                        onChange={( e ) =>
                          setText( e.target.value )
                        }
                      />
                      <button
                        type="button"
                        data-copy-value={text}
                        onClick={( e ) =>
                          copyToClipboard(
                            e.target.getAttribute(
                              'data-copy-value'
                            )
                          )
                        }
                        className="share__button"
                      >
                        {copyIcon} Copy HTML
                      </button>
                    </div>
                  </div>

                  <div className={`embed-preview ${activeEmbed}`}>
                    {isDesktop && <span className="share__subheader">Preview:</span>}
                    <div
                      className={`embed-preview-mini ${
                        activeEmbed === 'mini'
                          ? 'active'
                          : ''
                      }`}
                    >
                      <div className="embed-mini-right">
                        <div className="embed-mini-player">
                          <div
                            className="embed-mini-player-cover"
                            style={{
                              backgroundImage: `url(${getCoverImage})`,
                            }}
                          ></div>

                          <div className="embed-mini-player-controls">
                            <a className="logo-button">
                              {prhaLogoColor}
                            </a>
                            <div
                              className="play-button"
                              id="player_mini"
                            >
                              {playButton}
                            </div>
                            <div className="share-button">
                              {shareArrowIcon}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`embed-preview-big ${
                        activeEmbed === 'big'
                          ? 'active'
                          : ''
                      }`}
                    >
                      <div className="embed-player">
                        <div className="embed-big-player">
                          <div className="embed-big-player-cover">
                            <img
                              className="embed-big-player-img"
                              src={`${getCoverImage}?height=200`}
                            />
                          </div>
                          <div className="embed-big-player-info">
                            <span className="embed-player-title">
                              {decodeURIComponent( encodedTitle )}
                            </span>
                            <span className="embed-player-author">
                              Written by: {decodeURIComponent( author )}
                            </span>
                            { narratorsString && (
                              <span className="embed-player-narrator">
                                Read by: {narratorsString}
                              </span>
                            )}
                            <div className="embed-big-player-buttons">
                              <div className="embed-big-player-play-share">
                                <div
                                  className="play-button"
                                  id="player_big"
                                >
                                  {playButton}
                                </div>
                                <div
                                  className="share-button"
                                  style={{
                                    color: 'white',
                                  }}
                                >
                                  {shareArrowIcon}
                                </div>
                              </div>
                              <a className="logo-button">
                                {prhLogoBlack}
                              </a>
                            </div>
                            <hr></hr>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareModalMain;
